import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo } from "react";
import { useFormTcfVendor, useI18n } from "../../../../contexts";
import { FormHeadline } from "../../headline";
import { FORM_TCF_VENDOR_FIELD_PURPOSES_COLUMN_NAME_WIDTH, FormLayoutTcfVendor } from "..";
import { getTcfCookieTypeLocalization } from "@devowl-wp/react-cookie-banner";
import { Empty, Table, Tag, Tooltip } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { ETcfGvlDisclosureType } from "@devowl-wp/api-real-cookie-banner";
const { Column } = Table;
const FormTcfVendorFieldDeviceStorage = ()=>{
    const { __, _i, _n } = useI18n();
    const { vendor: { usesCookies, usesNonCookieAccess, cookieMaxAgeSeconds, cookieRefresh, deviceStorageDisclosureUrl, deviceStorageDisclosureViolation, deviceStorageDisclosure }, declarations: { purposes: allPurposes } } = useFormTcfVendor();
    const useDeviceStorageDisclosure = useMemo(()=>{
        const result = (deviceStorageDisclosure == null ? void 0 : deviceStorageDisclosure.disclosures.length) ? [
            ...deviceStorageDisclosure.disclosures
        ] : [];
        // Include implicit device storage into table
        if (usesNonCookieAccess) {
            result.unshift({
                type: ETcfGvlDisclosureType.Web,
                identifier: "*",
                purposes: undefined,
                cookieRefresh: undefined,
                domain: "*",
                maxAgeSeconds: null
            });
        }
        if (usesCookies) {
            result.unshift({
                type: ETcfGvlDisclosureType.Cookie,
                identifier: "*",
                purposes: undefined,
                cookieRefresh,
                domain: "*",
                maxAgeSeconds: cookieMaxAgeSeconds
            });
        }
        return result;
    }, [
        usesNonCookieAccess,
        usesCookies,
        cookieMaxAgeSeconds,
        cookieRefresh,
        deviceStorageDisclosure
    ]);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs(FormHeadline, {
                offset: FormLayoutTcfVendor.labelCol.span,
                description: _i(__("It should be specified all cookies, which are used by using a service of a TCF vendor. There are several types of cookies and that the {{aEprivacy}}ePrivacy Directive (Directive 2009/136/EC) Rectial 66{{/aEprivacy}} requires that you inform your visitors not only about (HTTP) cookies, but also about cookie-like information. This data, if specified, is given by the TCF Vendor and is not mutable. If the information is incomplete, you will need to contact the TCF vendor to request the information be completed."), {
                    aEprivacy: /*#__PURE__*/ _jsx("a", {
                        href: __("https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=celex:32009L0136"),
                        target: "_blank",
                        rel: "noreferrer"
                    })
                }),
                children: [
                    __("Device Storage Disclosure"),
                    " (",
                    __("Technical cookie information"),
                    ")"
                ]
            }),
            deviceStorageDisclosureViolation ? /*#__PURE__*/ _jsxs("div", {
                className: "notice notice-error inline below-h2 notice-alt",
                style: {
                    margin: "10px 0"
                },
                children: [
                    /*#__PURE__*/ _jsx("p", {
                        children: _i(__('TCF vendors must disclose their cookies, among other things, in accordance with the {{a}}"Vendor Device Storage & Operational Disclosures"{{/a}} specification. However, this vendor fails to comply with the specification, so that the mandatory information for obtaining informed consent as defined by the GDPR cannot be read. You as a website operator can therefore not obtain valid consent for this vendor.'), {
                            a: /*#__PURE__*/ _jsx("a", {
                                href: "https://github.com/InteractiveAdvertisingBureau/GDPR-Transparency-and-Consent-Framework/blob/7c79f48b033f783d98da922152430657097f5ab2/TCFv2/Vendor%20Device%20Storage%20&%20Operational%20Disclosures.md",
                                target: "_blank",
                                rel: "noreferrer"
                            })
                        })
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        children: _i(__("You can find the vendor's faulty device storage disclosure at {{a}}%s{{/a}}. Please contact the vendor and ask for a standard compliant device disclosures!", deviceStorageDisclosureUrl), {
                            a: /*#__PURE__*/ _jsx("a", {
                                href: deviceStorageDisclosureUrl,
                                target: "_blank",
                                rel: "noreferrer"
                            })
                        })
                    }),
                    /*#__PURE__*/ _jsxs("p", {
                        children: [
                            /*#__PURE__*/ _jsx("strong", {
                                children: __("Problem:")
                            }),
                            " ",
                            deviceStorageDisclosureViolation === "no-disclosures" ? __("The vendor does not provide any disclosures.") : deviceStorageDisclosureViolation === "disclosure-no-domains" ? __("The vendor does not specify for one or multiple cookies for which domains they are valid.") : deviceStorageDisclosureViolation === "disclosure-no-purposes" ? __("The vendor does not specify the purpose for one or multiple cookies.") : deviceStorageDisclosureViolation === "disclosure-no-cookie-refresh" ? __("The vendor does not specify for one or multiple cookies if the cookie does refresh.") : deviceStorageDisclosureViolation === "disclosure-no-max-age-seconds" ? __("The vendor does not specify the age in seconds for one or multiple cookies.") : __("The vendor provides a technically non-machine-readable variant of the data, which differs significantly from the defined standard.")
                        ]
                    })
                ]
            }) : /*#__PURE__*/ _jsxs(Table, {
                locale: {
                    emptyText: /*#__PURE__*/ _jsx(Empty, {
                        description: __("This vendor does not provide any device storage disclosure.")
                    })
                },
                dataSource: useDeviceStorageDisclosure,
                pagination: {
                    pageSize: 15,
                    showTotal: (total, range)=>`${range[0]}-${range[1]} / ${total}`,
                    showSizeChanger: false
                },
                rowKey: (param)=>{
                    let { identifier, type } = param;
                    return `${type}${identifier}`;
                },
                size: "small",
                bordered: true,
                children: [
                    /*#__PURE__*/ _jsx(Column, {
                        width: FORM_TCF_VENDOR_FIELD_PURPOSES_COLUMN_NAME_WIDTH,
                        title: __("Cookie type"),
                        dataIndex: "cookieType",
                        render: (_, param)=>{
                            let { type } = param;
                            return getTcfCookieTypeLocalization(type);
                        }
                    }, "cookieType"),
                    /*#__PURE__*/ _jsx(Column, {
                        title: __("Identifier"),
                        dataIndex: "identifier",
                        render: (_, param)=>{
                            let { identifier } = param;
                            return identifier ? /*#__PURE__*/ _jsx("code", {
                                children: identifier
                            }) : __("Not defined");
                        }
                    }, "identifier"),
                    /*#__PURE__*/ _jsx(Column, {
                        title: __("Domain"),
                        dataIndex: "domain",
                        render: (_, param)=>{
                            let { domains, domain } = param;
                            return domains ? /*#__PURE__*/ _jsx("code", {
                                children: domains.join(",")
                            }) : domain ? /*#__PURE__*/ _jsx("code", {
                                children: domain
                            }) : __("Not defined");
                        }
                    }, "domain"),
                    /*#__PURE__*/ _jsx(Column, {
                        title: __("Duration"),
                        dataIndex: "duration",
                        render: (_, param)=>{
                            let { maxAgeSeconds, cookieRefresh } = param;
                            return maxAgeSeconds !== null ? /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    maxAgeSeconds <= 0 ? /*#__PURE__*/ _jsx(Tooltip, {
                                        title: __("This cookie is active as long as the session is active"),
                                        children: /*#__PURE__*/ _jsx("span", {
                                            children: __("Session")
                                        })
                                    }) : /*#__PURE__*/ _jsxs("span", {
                                        children: [
                                            maxAgeSeconds,
                                            " ",
                                            __("second(s)")
                                        ]
                                    }),
                                    cookieRefresh && /*#__PURE__*/ _jsx(Tag, {
                                        icon: /*#__PURE__*/ _jsx(ReloadOutlined, {}),
                                        style: {
                                            marginLeft: 10
                                        },
                                        children: __("Refresh")
                                    })
                                ]
                            }) : __("Not defined");
                        }
                    }, "duration"),
                    /*#__PURE__*/ _jsx(Column, {
                        title: __("Purposes"),
                        dataIndex: "purposes",
                        render: (_, param)=>{
                            let { purposes } = param;
                            return purposes ? purposes.length ? /*#__PURE__*/ _jsx(Tooltip, {
                                title: /*#__PURE__*/ _jsx("ul", {
                                    style: {
                                        margin: 0,
                                        padding: 0
                                    },
                                    children: purposes.map((purposeId)=>/*#__PURE__*/ _jsx("li", {
                                            children: allPurposes[purposeId].name
                                        }, purposeId))
                                }),
                                children: _n("%d purpose", "%d purposes", purposes.length, purposes.length)
                            }) : __("None") : __("Unknown");
                        }
                    }, "purposes")
                ]
            })
        ]
    });
};
export { FormTcfVendorFieldDeviceStorage };
